import React from 'react';
import { Check, Zap, Shield, TrendingUp, Users } from 'lucide-react';

const ProductShowcase: React.FC = () => {
  const handleWhatsAppDemo = () => {
    const phoneNumber = "919384376329";
    const message = "Hi! I'm interested in learning more about Park Hive parking management system. Can you please provide me with a demo?";
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const benefits = [
    {
      icon: <Zap className="h-12 w-12 text-blue-600" />,
      title: 'Less waiting. Less confusion. Less revenue loss.',
      description: 'Automated systems eliminate bottlenecks and accelerate vehicle movement for smoother operations.',
      features: ['Instant entry/exit scanning', 'Automated billing calculation', 'Digital payment processing']
    },
    {
      icon: <Shield className="h-12 w-12 text-green-600" />,
      title: 'More control. More trust. More speed.',
      description: 'Complete operational visibility with real-time monitoring and transparent revenue tracking.',
      features: ['Live dashboard monitoring', 'Staff accountability tracking', 'Exportable audit reports']
    },
    {
      icon: <TrendingUp className="h-12 w-12 text-purple-600" />,
      title: 'Data-driven decisions. Frictionless experience.',
      description: 'Every operational decision becomes transparent and verifiable for confident business management.',
      features: ['Real-time analytics', 'Revenue optimization', 'Performance insights']
    },
    {
      icon: <Users className="h-12 w-12 text-orange-600" />,
      title: 'Professional operations. Scalable growth.',
      description: 'Built for mobility and designed for everyday complexity with future-ready technology.',
      features: ['Multi-location support', 'Offline capability', 'Team management']
    }
  ];

  return (
    <section id="pricing" className="py-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Why Parking Owners Prefer Park Hive
          </h2>
          <p className="text-lg text-gray-600">
            Because it doesn't demand months of installation or heavy investment — it works with the simplicity of a mobile app. You no longer manage parking. You command it.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {benefits.map((benefit, index) => (
            <div key={index} className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200 hover:shadow-2xl transition-shadow duration-300">
              <div className="flex items-center mb-6">
                <div className="mr-4">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900">{benefit.title}</h3>
              </div>
              <p className="text-gray-600 mb-6">{benefit.description}</p>
              <ul className="space-y-3">
                {benefit.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8 md:p-12 text-center">
          <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
            Where It Delivers the Highest Impact
          </h3>
          <p className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto">
            Whether it's the weekend rush at a multiplex, emergency movement in a hospital, commercial peak at corporate parks, festival crowd at tourist attractions, or visitor control in residential communities — Park Hive keeps movement smooth, safe, and fully accountable.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={handleWhatsAppDemo}
              className="bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-medium hover:bg-blue-700 transition-colors duration-200"
            >
              Book a Live Demo
            </button>
            <button 
              onClick={handleWhatsAppDemo}
              className="border border-gray-300 text-gray-700 px-8 py-4 rounded-lg text-lg font-medium hover:bg-white transition-colors duration-200"
            >
              Request a Demo
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductShowcase;